"use client";

import Image from "next/image";
import { usePathname } from "next/navigation";
import type { ReactNode } from "react";
import { useEffect, useRef } from "react";
import type { FallbackProps } from "react-error-boundary";
import { ErrorBoundary } from "react-error-boundary";

import { EmptyState } from "@/components/shared/empty-state";
import { Button } from "@/components/ui/button";
import { useLayout } from "@/contexts/layout-context";
import { cn } from "@/utils/lib";

function Fallback({ error, resetErrorBoundary }: FallbackProps) {
  // We keep this log to have access to it in devtools
  console.error(error);
  const errorDetails =
    error instanceof Error
      ? (error.stack ?? error.message)
      : typeof error === "string"
        ? error
        : JSON.stringify(error, null, 2);

  const pathname = usePathname();
  const initialPathNameRef = useRef(pathname);

  useEffect(() => {
    if (initialPathNameRef.current !== pathname) {
      resetErrorBoundary();
    }
  }, [pathname, resetErrorBoundary]);

  return (
    <div
      className="
        flex h-safe-screen w-full flex-col items-center justify-center gap-6 p-6
      "
    >
      <EmptyState
        image="/assets/empty-38.png"
        title="Hmmm, looks like we lost focus!"
        description="We're really sorry this happened. We will eliminate these distractions as soon as possible."
      />
      <div className="flex items-center gap-4">
        <Button onClick={() => window.location.reload()}>Refresh</Button>{" "}
        <Button onClick={resetErrorBoundary}>Retry</Button>
      </div>
      <details className="w-full text-sm text-muted">
        <summary
          className="
          cursor-pointer text-center underline underline-offset-4
        "
        >
          Error details
        </summary>
        <pre
          className="
            mt-4 flex max-h-[400px] w-full overflow-scroll rounded-sm border
            border-border-light p-4 font-mono
          "
        >
          {errorDetails}
        </pre>
      </details>
    </div>
  );
}

export const MainLayoutWrapper = ({ children }: { children: ReactNode }) => {
  const { rightSidebar } = useLayout();

  return (
    <div
      className={cn("unforce-radius w-full", {
        "border-border sm:border-r lg:w-[calc(100%-var(--size-left-sidebar)-var(--size-right-sidebar))]":
          rightSidebar,
        "h-full border-r border-border lg:w-[calc(100%-var(--size-left-sidebar))]":
          !rightSidebar,
      })}
    >
      <ErrorBoundary FallbackComponent={Fallback}>{children}</ErrorBoundary>
      {rightSidebar && (
        <div
          className="
            fixed right-6 bottom-6 hidden h-[53px] w-[60px] opacity-100
            xl:inline-block
          "
        >
          <Image
            src="/assets/logo-focus-whale.svg"
            alt="whaley"
            fill
            priority
          />
        </div>
      )}
    </div>
  );
};
